import { formatApiDetail } from '../utils/apiError'

export const API_BASE = 'https://api.cityseecityfix.com/api/v1'

export const REPORT_ERROR = {
  NOT_FOUND: 'not_found',
  INVALID_OTP: 'invalid_otp',
  NOT_READY: 'not_ready',
  UNKNOWN: 'unknown',
}

export class ReportAccessError extends Error {
  constructor(message, { kind = REPORT_ERROR.UNKNOWN, status = 0 } = {}) {
    super(message)
    this.name = 'ReportAccessError'
    this.kind = kind
    this.status = status
  }
}

export class LeadApiError extends Error {
  constructor(message, { status = 0 } = {}) {
    super(message)
    this.name = 'LeadApiError'
    this.status = status
  }
}

export const reportAccessKey = (jobId) => `csc-report-access-${jobId}`
export const reportDataKey = (jobId) => `csc-report-${jobId}`
const leadSessionKey = 'csc-lead-session'

/**
 * Accept numeric lead IDs or UUID job IDs for report deep links / status polling.
 * Never treat an email address as a lead reference.
 */
export const normalizeLeadReference = (value) => {
  if (value == null) return null
  const normalized = String(value).trim()
  if (!normalized || normalized.includes('@')) return null
  if (/^\d+$/.test(normalized)) return normalized
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      normalized,
    )
  ) {
    return normalized
  }
  return null
}

export const firstLeadReference = (...values) => {
  for (const value of values) {
    const normalized = normalizeLeadReference(value)
    if (normalized) return normalized
  }
  return null
}

export const grantReportAccess = (jobId, reportData) => {
  sessionStorage.setItem(reportAccessKey(jobId), '1')
  if (reportData) {
    sessionStorage.setItem(reportDataKey(jobId), JSON.stringify(reportData))
  }
}

export const clearReportAccess = (jobId) => {
  sessionStorage.removeItem(reportAccessKey(jobId))
  sessionStorage.removeItem(reportDataKey(jobId))
}

export const hasReportAccess = (jobId) =>
  sessionStorage.getItem(reportAccessKey(jobId)) === '1'

export const getStoredReport = (jobId) => {
  try {
    const raw = sessionStorage.getItem(reportDataKey(jobId))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const clearLeadSession = () => {
  sessionStorage.removeItem(leadSessionKey)
}

export const saveLeadSession = (session) => {
  const email = session?.email?.trim()
  if (!email) {
    clearLeadSession()
    return
  }

  sessionStorage.setItem(
    leadSessionKey,
    JSON.stringify({
      email,
      leadId: normalizeLeadReference(session.leadId),
      jobId: session.jobId ?? null,
      domain: session.domain ?? null,
      validationFailed: Boolean(session.validationFailed),
      failureMessage: session.failureMessage ?? null,
    }),
  )
}

export const getLeadSession = () => {
  try {
    const raw = sessionStorage.getItem(leadSessionKey)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const isOtpRetryableError = (kind) =>
  kind === REPORT_ERROR.INVALID_OTP || kind === REPORT_ERROR.UNKNOWN

export const classifyReportError = (status, message) => {
  const msg = String(message ?? '').toLowerCase()

  if (status === 404 || msg.includes('not found')) {
    return REPORT_ERROR.NOT_FOUND
  }
  if (
    status === 401 ||
    status === 403 ||
    msg.includes('invalid') ||
    msg.includes('incorrect') ||
    msg.includes('wrong code') ||
    (msg.includes('otp') && (msg.includes('invalid') || msg.includes('expired')))
  ) {
    return REPORT_ERROR.INVALID_OTP
  }
  if (
    status === 202 ||
    status === 425 ||
    msg.includes('not ready') ||
    msg.includes('still preparing') ||
    msg.includes('in progress') ||
    msg.includes('being prepared')
  ) {
    return REPORT_ERROR.NOT_READY
  }

  return REPORT_ERROR.UNKNOWN
}

export const userMessageForReportError = (kind, fallbackMessage) => {
  switch (kind) {
    case REPORT_ERROR.NOT_FOUND:
      return 'This city discovery report is not available yet. It may still be preparing, or the link may have expired.'
    case REPORT_ERROR.INVALID_OTP:
      return 'That access code did not work. Check your email and try again.'
    case REPORT_ERROR.NOT_READY:
      return 'Your city discovery report is still being prepared. We will email you when it is ready.'
    default:
      return fallbackMessage || 'Unable to load report.'
  }
}

export const extractDigits = (value) =>
  String(value ?? '').replace(/\D/g, '').slice(0, 6)

/** Encode domain for report API path: dots → underscores */
export const encodeDomainForReport = (domain) =>
  String(domain ?? '').replace(/\./g, '_')

/** API: GET /api/v1/report/{encoded_domain}?otp={code}&em={email} */
export const buildReportApiUrl = (domain, otp, email) => {
  const encodedDomain = encodeDomainForReport(domain)
  const params = new URLSearchParams({
    otp,
    em: email,
  })
  return `${API_BASE}/report/${encodeURIComponent(encodedDomain)}?${params.toString()}`
}

/** POST /api/v1/leads/preview/otp — generate or refresh preview access code */
export const requestPreviewOtp = async (email) => {
  const response = await fetch(`${API_BASE}/leads/preview/otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })

  if (!response.ok) {
    let message = 'Unable to send access code'
    try {
      const body = await response.json()
      message = formatApiDetail(body.detail, message)
    } catch {
      // ignore parse errors
    }
    throw new Error(message)
  }

  return response.json()
}

export const isReportReadyStatus = (status) =>
  ['completed', 'complete'].includes(String(status ?? '').toLowerCase())

const DOMAIN_VALIDATION_FAILURE_MESSAGE =
  'Domain failed validation. Was it a typo?'

export const isValidationFailedStatus = (status) =>
  ['error', 'failed'].includes(String(status ?? '').toLowerCase())

/** OTP/preview errors that mean validation failed (or never produced a preview), not "still cooking". */
export const isPreviewUnavailableFailure = (message) => {
  const msg = String(message ?? '').toLowerCase()
  if (!msg) return false
  if (msg.includes('preview not found')) return true
  if (msg.includes('authority') && msg.includes('not found')) return true
  if (msg.includes('validation failed')) return true
  if (msg.includes('domain failed validation')) return true
  return false
}

export const isPreviewNotReadyMessage = (message) => {
  const msg = String(message ?? '').toLowerCase()
  return (
    msg.includes('not complete yet') ||
    msg.includes('not ready') ||
    msg.includes('still preparing') ||
    msg.includes('in progress') ||
    msg.includes('being prepared')
  )
}

export const messageForPreviewOtpFailure = (errorMessage) => {
  if (isPreviewUnavailableFailure(errorMessage)) {
    return DOMAIN_VALIDATION_FAILURE_MESSAGE
  }
  if (isPreviewNotReadyMessage(errorMessage)) {
    return (
      firstNonEmptyDetail(errorMessage) ||
      'Your city discovery report is still being prepared. We will email you when it is ready.'
    )
  }
  return (
    firstNonEmptyDetail(errorMessage) ||
    'Unable to send access code. Please try again or use a different city email.'
  )
}

const firstNonEmptyDetail = (value) => {
  const text = String(value ?? '').trim()
  return text || null
}

export { DOMAIN_VALIDATION_FAILURE_MESSAGE }

export const fetchLeadStatus = async (jobId) => {
  const response = await fetch(`${API_BASE}/leads/${jobId}/status`)
  if (!response.ok) {
    let message = `Unable to check report status (${response.status})`
    try {
      const body = await response.json()
      message = formatApiDetail(body.detail, message)
    } catch {
      // ignore parse errors
    }
    throw new LeadApiError(message, { status: response.status })
  }
  return response.json()
}

/** POST /api/v1/leads — returns raw response envelope for caller to interpret. */
export const submitLead = async (email) => {
  const response = await fetch(`${API_BASE}/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })

  let data = null
  try {
    data = await response.json()
  } catch {
    // ignore empty or invalid JSON bodies
  }

  return { ok: response.ok, status: response.status, data }
}

export const fetchLeadCrawlReport = async (domain, otp, email) => {
  const response = await fetch(buildReportApiUrl(domain, otp, email))
  if (!response.ok) {
    let message = `Failed to load report (${response.status})`
    try {
      const body = await response.json()
      message = formatApiDetail(body.detail, message)
    } catch {
      // ignore parse errors
    }
    const kind = classifyReportError(response.status, message)
    throw new ReportAccessError(userMessageForReportError(kind, message), {
      kind,
      status: response.status,
    })
  }
  return response.json()
}

/** Frontend deep link: /cities/report/{domain}?lead={job_id}&otp={code}&em={email} */
export const buildReportPageUrl = (domain, leadId, otp, email) => {
  const path = `/cities/report/${encodeURIComponent(domain)}`
  const params = new URLSearchParams({ lead: leadId })
  if (otp) params.set('otp', otp)
  if (email) params.set('em', email)
  return `${path}?${params.toString()}`
}
